import React, { useState, useCallback, useEffect } from "react";
import { Spin } from 'antd';
import PopeyeHeader from "./PopeyeHeader/PopeyeHeader";
import PopeyeContent from "./PopeyeContent/PopeyeContent";
import PopeyeNews from "./PopeyeNews/PopeyeNews";
import PopeyeDeals from "./PopeyeDeals/PopeyeDeals";
import { useLocation } from 'react-router-dom';
import PopeyeAttentions from "./PopeyeAttentions/PopeyeAttentions";
import { useFetch } from "../../utils/hooks";
import { getPopeyeShipmentsListTotals, getPopeyeShipmentsList } from "../../services/shipments";
import "./Popeye.scss";
import PopeyeShipmentMap from "../ShipmentMap/PopeyeShipmentMap";

const Popeye = (props) => {
  const [bookingList, setBookingList] = useState([]);
  const [currentComponent, setCurrentComponent] = useState(0); // Start with the first component
  const [page, setPage] = useState(1);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [showContent, setShowContent] = useState(true); // State to toggle visibility between content and map

  const location = useLocation();
  // Extract "as" parameter from URL
  const searchParams = new URLSearchParams(location.search);
  const asParam = searchParams.get('as'); // This will extract the "as" parameter

   // Helper function to calculate duration based on length
   const calculateDuration = (length, multiplier = 3, base = 10, threshold = 20) => {
    return (length < threshold ? length * multiplier + base : length) / 60;
  };
  
  // Fetching totals
  const { data: totalsData, loading: totalsLoading, refetch: refetchTotals } = useFetch(
    useCallback(() => getPopeyeShipmentsListTotals({ bookingList,as: asParam  }), [bookingList])
  );

  // Fetching shipments list
  const { data: shipmentsData, loading: shipmentsLoading, refetch: refetchShipments } = useFetch(
    useCallback(() => {
      const params = {
        bookingList, 
        as: asParam  // Pass the "as" parameter from the URL
      };
      return getPopeyeShipmentsList(params, page);
    }, [page, bookingList])
  );

  // Calculate the interval for refetching shipments
  useEffect(() => {
    console.log(shipmentsData?.shipments?.length)
    if (shipmentsData?.shipments?.length) {
      const cycleDuration = process.env.REACT_APP_POPEYE_CYCLE_DURATION; // Duration in seconds for each shipment
      const totalShipments = shipmentsData.shipments.length;
      const totalDuration = cycleDuration * totalShipments * 1000; // Total duration in milliseconds

      // Set refetch interval
      const intervalId = setInterval(async () => {
        await refetchShipments();
        await refetchTotals(); // Refetch totals after refetching shipments
      }, Math.max(totalDuration, process.env.REACT_APP_POPEYE_CYCLE_MIN_DURATION * 1000));

      // Clear interval on component unmount
      return () => clearInterval(intervalId);
    }
  }, [shipmentsData]);

  // Toggle visibility between PopeyeContent and PopeyeShipmentMap every few minutes
  useEffect(() => {
    // if (shipmentsData) {
    //   const cycleDuration = process.env.REACT_APP_POPEYE_CYCLE_DURATION; // Duration in seconds for each shipment
    //   const totalShipments = shipmentsData.shipments.length;
    //   const totalDuration = cycleDuration * totalShipments * 1000; // Total duration in milliseconds

    //   const toggleInterval = setInterval(() => {
    //     setShowContent((prevShowContent) => !prevShowContent); // Toggle between true and false
    //   }, showContent ? Math.max(totalDuration, process.env.REACT_APP_POPEYE_CYCLE_MIN_DURATION * 1000) : process.env.REACT_APP_POPEYE_MAP_DURATION * 100000 * 1000); 
    //   // total duration to loop on all shipments first then switch to map
    //   // If there are few shipments, the minimum will be 60 seconds; otherwise, wait 2 minutes for map

    //   return () => clearInterval(toggleInterval); // Cleanup interval on component unmount
    // }
  }, [shipmentsData, showContent]);

  // Handle version check and refresh page if version changes
  useEffect(() => {
    if (shipmentsData) {
      const newVersion = shipmentsData.version;
      
      if (currentVersion && currentVersion !== newVersion) {
        window.location.reload();  // Version changed; refresh the page
      } else if (!currentVersion) {
        setCurrentVersion(newVersion);  // Initialize version
      }
    }
  }, [shipmentsData, currentVersion]);

  // Toggle visibility between PopeyeDeals, PopeyeAttentions, and PopeyeNews
  useEffect(() => {
    const dealsDuration = calculateDuration(shipmentsData?.deals?.length || 0);
    const updatesDuration = calculateDuration(shipmentsData?.updates?.length || 0);
    const newsDuration = calculateDuration(shipmentsData?.news?.length || 0);

    const componentDurations = [
      updatesDuration, // Duration for PopeyeAttentions
      newsDuration,    // Duration for PopeyeNews
      dealsDuration,   // Duration for PopeyeDeals
    ];

    // Remove dealsDuration if there are no deals
    if (shipmentsData?.deals?.length === 0) {
      componentDurations.pop(); // Removes the last element from the array
    }

    const switchComponent = () => {
      setCurrentComponent((prev) => (prev + 1) % componentDurations.length);
    };

    const interval = setInterval(() => {
      switchComponent();
    }, componentDurations[currentComponent] * 60 * 1000); // Convert duration to milliseconds

    return () => clearInterval(interval); // Cleanup interval on unmount or dependency change
  }, [currentComponent, shipmentsData]);

  // Consolidate totals
  const totals = {
    total: totalsData?.total,
    totalScheduled: totalsData?.total_scheduled,
    totalSailing: totalsData?.total_sailing,
    totalDischarged: totalsData?.total_discharged,
    totalDelayed: totalsData?.total_delayed,
  };

  if (totalsLoading || shipmentsLoading) {
    return <Spin className="spinner" size="large" />;
  }

  const shipments = shipmentsData?.shipments;
  const updates = shipmentsData?.updates;
  const news = shipmentsData?.news;
  const deals = shipmentsData?.deals;

  return (
    <div className="Popeye">
      <PopeyeHeader totals={totals} />
      <div className="Content-Map-Container">
        {showContent ? (
          <PopeyeContent shipments={shipments} bookingList={bookingList} />
        ) : (
          <PopeyeShipmentMap shipments={shipments} selectShipment={shipments[0]} />
        )}
      </div>
      <div>
        {currentComponent === 0 && shipmentsData?.updates?.length > 0 && <PopeyeAttentions updates={updates} />}
        {currentComponent === 1 && shipmentsData?.news?.length > 0 && <PopeyeNews news={news} />}
        {currentComponent === 2 && shipmentsData?.deals?.length > 0 && <PopeyeDeals deals={deals} />}
      </div>
    </div>
  );
};

export default Popeye;
